import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link, useNavigate } from "../lib/router";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { useMeta } from "../hooks/useMeta";
import { PageHeader } from "../components/PageHeader";
import { Overline, Reveal } from "../components/motion";
import { Field, PButton, Select, TextArea } from "../components/forms";
import { EditionSelector, ProductStage } from "../components/editions";
import { ALL_PRODUCTS, DEFAULT_EDITION, findEdition, findProduct, isReformer } from "../data/products";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STUDIO_PACKAGE = "studio-paket";
const WEB3FORMS_KEY = "7146e56a-6101-43bb-8c6b-54023812090a";

export default function Inquiry() {
  const { t, lang } = useLanguage();
  useMeta(t.seo.inquiry[0], t.seo.inquiry[1]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [product, setProduct] = useState(searchParams.get("produkt") || "");
  const [edition, setEdition] = useState(
    findEdition(searchParams.get("edition") || searchParams.get("finish") || DEFAULT_EDITION).code
  );
  const [quantity, setQuantity] = useState("1");
  const [category, setCategory] = useState("");
  const [form, setForm] = useState({ firstName: "", lastName: "", company: "", email: "", phone: "", location: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const selected = product === STUDIO_PACKAGE ? null : findProduct(product);
  const showFinishes = isReformer(product);
  const activeEdition = findEdition(edition);

  const submit = async (e) => {
    e.preventDefault();
    if (!product) {
      toast.error(`${t.common.required}: ${t.inquiry.product}`);
      return;
    }
    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.error(`${t.common.required}: ${t.inquiry.firstName} / ${t.inquiry.lastName}`);
      return;
    }
    if (!EMAIL_RE.test(form.email)) {
      toast.error(t.common.invalidEmail);
      return;
    }
    setSending(true);
    try {
      const productName = product === STUDIO_PACKAGE ? t.inquiry.studioPackage : selected ? selected.name : "–";
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: "Neue Angebotsanfrage – PILATES ON.",
          from_name: "PILATES ON. Website",
          anfrage_typ: "Angebotsanfrage",
          produkt: productName,
          edition: showFinishes ? activeEdition.label.de : "–",
          stueckzahl: quantity || "–",
          kundentyp: category || "–",
          vorname: form.firstName,
          nachname: form.lastName,
          unternehmen: form.company || "–",
          email: form.email,
          telefon: form.phone || "–",
          ort: form.location || "–",
          nachricht: form.message || "–",
          sprache: lang,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error("Form submission failed");
      navigate("/danke");
    } catch {
      toast.error(t.common.formError);
      setSending(false);
    }
  };

  return (
    <div data-testid="inquiry-page">
      <PageHeader id="inquiry" overline={t.inquiry.overline} lines={t.inquiry.title} sub={t.inquiry.sub} />
      <section className="mx-auto max-w-[1400px] px-6 pb-32 md:px-12 lg:px-20">
        <div className="grid gap-16 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div data-testid="inquiry-summary" className="bg-[#F3EFEA] p-8 lg:sticky lg:top-28 md:p-10">
              <Overline className="mb-6">{t.inquiry.summary}</Overline>
              {selected ? (
                <>
                  {showFinishes ? <ProductStage product={selected} edition={edition} testId="inquiry-stage" className="aspect-[4/3] p-6" /> : <div className="aspect-[4/3] overflow-hidden" style={{ backgroundColor: selected.backdrop }}><img src={selected.image} alt={selected.name} className={`h-full w-full ${selected.cover ? "object-cover" : "object-contain p-6"}`} /></div>}
                  <p data-testid="inquiry-summary-product" className="mt-6 font-serif text-2xl font-light text-[#1C1C1A]">{selected.name}</p>
                  {showFinishes && <p data-testid="inquiry-summary-finish" className="mt-3 flex items-center gap-3 text-sm text-[#66625A]"><span className="inline-block h-4 w-4 rounded-full ring-1 ring-black/10" style={{ background: activeEdition.swatch }} />{activeEdition.label[lang]}</p>}
                  <p data-testid="inquiry-summary-quantity" className="mt-2 text-sm text-[#66625A]">{t.inquiry.quantity}: {quantity}</p>
                  <p data-testid="inquiry-summary-price" className="mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">{t.common.priceOnRequest}</p>
                </>
              ) : product === STUDIO_PACKAGE ? <p data-testid="inquiry-summary-product" className="font-serif text-2xl font-light text-[#1C1C1A]">{t.inquiry.studioPackage}</p> : <p data-testid="inquiry-summary-empty" className="text-sm font-light text-[#66625A]">{t.inquiry.noProduct}</p>}
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-8">
            <form onSubmit={submit} className="grid gap-x-12 gap-y-10 md:grid-cols-2" noValidate>
              <Select label={t.inquiry.product} id="inquiry-product" value={product} onChange={(e) => setProduct(e.target.value)} required>
                <option value="">{t.inquiry.select}</option>
                {ALL_PRODUCTS.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                <option value={STUDIO_PACKAGE}>{t.inquiry.studioPackage}</option>
              </Select>
              <Field label={t.inquiry.quantity} id="inquiry-quantity" type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} required />
              {showFinishes && <div className="md:col-span-2"><p className="mb-6 font-mono text-[10px] uppercase tracking-[0.25em] text-[#66625A]">{t.inquiry.finish}</p><EditionSelector value={edition} onChange={setEdition} size="sm" testPrefix="inquiry-edition" className="gap-6" /></div>}
              <div className="md:col-span-2">
                <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#66625A]">{t.inquiry.category}</p>
                <div role="radiogroup" data-testid="inquiry-category" className="flex flex-wrap gap-x-8 gap-y-4">
                  {t.inquiry.categories.map((c) => { const active = category === c; return <button type="button" role="radio" aria-checked={active} key={c} data-testid={`inquiry-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`} onClick={() => setCategory(c)} className="group flex items-center gap-3 text-[13px] text-[#1C1C1A]"><span className={`flex h-4 w-4 items-center justify-center rounded-full border transition-colors duration-300 ${active ? "border-[#1C1C1A]" : "border-[#1C1C1A]/30 group-hover:border-[#1C1C1A]"}`}><span className={`h-2 w-2 rounded-full bg-[#1C1C1A] transition-transform duration-300 ${active ? "scale-100" : "scale-0"}`} /></span>{c}</button>; })}
                </div>
              </div>
              <div className="md:col-span-2 mt-4"><Overline className="mb-2">{t.inquiry.contactData}</Overline></div>
              <Field label={t.inquiry.firstName} id="inquiry-first-name" value={form.firstName} onChange={set("firstName")} required />
              <Field label={t.inquiry.lastName} id="inquiry-last-name" value={form.lastName} onChange={set("lastName")} required />
              <Field label={t.inquiry.company} id="inquiry-company" value={form.company} onChange={set("company")} />
              <Field label={t.inquiry.email} id="inquiry-email" type="email" value={form.email} onChange={set("email")} required />
              <Field label={t.inquiry.phone} id="inquiry-phone" type="tel" value={form.phone} onChange={set("phone")} />
              <Field label={t.inquiry.location} id="inquiry-location" value={form.location} onChange={set("location")} />
              <div className="md:col-span-2"><TextArea label={t.inquiry.message} id="inquiry-message" value={form.message} onChange={set("message")} /></div>
              <div className="md:col-span-2"><PButton type="submit" variant="solid" arrow={false} disabled={sending} data-testid="inquiry-form-submit">{sending ? t.common.sending : t.inquiry.submit}</PButton>
                <p className="mt-4 max-w-2xl text-xs font-light leading-relaxed text-[#66625A]">Mit dem Absenden werden deine Angaben zur Bearbeitung deiner Anfrage verarbeitet. Weitere Informationen findest du in unserer <Link to="/datenschutz" className="underline underline-offset-2 transition-colors hover:text-[#C5A059]">Datenschutzerklärung</Link>.</p></div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
